import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Star, Tent, TreePine, Car, Waves, MapPin, Navigation, X, Lock, Sparkles } from 'lucide-react';
import { CampingSpot, Coordinates } from '../../types';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../services/firebaseService';

const DESKTOP_CARD_WIDTH = 280;
const CARD_GAP = 12;

const SATELLITE_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

type MiniCardPlatform = 'mobile' | 'tablet' | 'desktop';

const PREF_KEYS: Record<MiniCardPlatform, string> = {
  mobile: 'fricamp_mini_cards_mobile',
  tablet: 'fricamp_mini_cards_tablet',
  desktop: 'fricamp_mini_cards_desktop',
};

// Legacy key — used for migration only
const LEGACY_PREF_KEY = 'fricamp_mini_cards_visible';

/** Detect current device platform */
export const detectMiniCardPlatform = (): MiniCardPlatform => {
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  if (!hasCoarsePointer) return 'desktop';
  const shortestSide = Math.min(window.innerWidth, window.innerHeight);
  return shortestSide >= 600 ? 'tablet' : 'mobile';
};

/** Read pref for a specific platform, with legacy migration */
const getPlatformPref = (platform: MiniCardPlatform): boolean => {
  try {
    const val = localStorage.getItem(PREF_KEYS[platform]);
    if (val !== null) return val !== 'false';
    // Migrate from legacy single-toggle
    const legacy = localStorage.getItem(LEGACY_PREF_KEY);
    if (legacy !== null) return legacy !== 'false';
    return true; // default enabled
  } catch { return true; }
};

/** Generate a satellite thumbnail URL for a given coordinate at zoom 15 */
const getSatelliteUrl = (lat: number, lon: number): string => {
  const z = 15;
  const n = Math.pow(2, z);
  const x = Math.floor(((lon + 180) / 360) * n);
  const y = Math.floor(
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n
  );
  return SATELLITE_TILE.replace('{z}', String(z)).replace('{y}', String(y)).replace('{x}', String(x));
};

/** Calculate pin position (%) within the satellite tile for a given coordinate */
const getSatellitePinPosition = (lat: number, lon: number): { left: number; top: number } => {
  const z = 15;
  const n = Math.pow(2, z);
  const xTile = ((lon + 180) / 360) * n;
  const yTile = ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n;
  return {
    left: (xTile - Math.floor(xTile)) * 100,
    top: (yTile - Math.floor(yTile)) * 100,
  };
};

/** Calculate CSS offsets to center a point within a container using a 256px tile */
const getSatelliteOffset = (pinLeftPct: number, pinTopPct: number, containerPx: number) => {
  const tile = 256;
  const pinPxX = pinLeftPct / 100 * tile;
  const pinPxY = pinTopPct / 100 * tile;
  const half = containerPx / 2;
  return {
    left: Math.min(0, Math.max(-(tile - containerPx), half - pinPxX)),
    top: Math.min(0, Math.max(-(tile - containerPx), half - pinPxY)),
  };
};

interface MapMiniCardsProps {
  spots: CampingSpot[];
  selectedSpotId: string | null;
  onSelectSpot: (id: string) => void;
  onNavigate: (spot: CampingSpot) => void;
  userLocation?: Coordinates;
  /** Antall premium-spots (algoritmiske gratisplasser) som er filtrert bort
   *  fra `spots` for gratisbrukere. Hvis > 0 vises en teaser-kort på slutten. */
  hiddenPremiumCount?: number;
  /** Trigges når brukeren trykker på premium-teaser-kortet. */
  onUpgrade?: () => void;
  /** Bredden på venstre sidebar-rail (px) så kortene ikke legger seg oppå den når den er utvidet. */
  leftOffsetPx?: number;
}

const getSpotIcon = (spot: CampingSpot, size = 14) => {
  if (spot.spotType === 'rasteplass') return <Car size={size} className="text-blue-400" />;
  if (spot.spotType === 'commercial_campsite') return <Tent size={size} className="text-amber-400" />;
  if (spot.spotType === 'coastal') return <Waves size={size} className="text-cyan-400" />;
  if (spot.features?.includes('GAPAHUK')) return <TreePine size={size} className="text-emerald-400" />;
  if (spot.features?.includes('BADEPLASS')) return <Waves size={size} className="text-cyan-400" />;
  return <MapPin size={size} className="text-emerald-400" />;
};

const formatDistance = (spot: CampingSpot, userLocation?: Coordinates): string | null => {
  if (!userLocation) return null;
  const R = 6371;
  const dLat = (spot.location.latitude - userLocation.latitude) * Math.PI / 180;
  const dLon = (spot.location.longitude - userLocation.longitude) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(userLocation.latitude * Math.PI / 180) *
    Math.cos(spot.location.latitude * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  if (km < 1) return `${Math.round(km * 1000)}m`;
  if (km < 100) return `${km.toFixed(1)}km`;
  return `${Math.round(km)}km`;
};

const MapMiniCards: React.FC<MapMiniCardsProps> = ({
  spots,
  selectedSpotId,
  onSelectSpot,
  onNavigate,
  userLocation,
  hiddenPremiumCount = 0,
  onUpgrade,
  leftOffsetPx = 56,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);

  const [visible, setVisible] = useState(() => getPlatformPref(detectMiniCardPlatform()));

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 640);
  const [maxCards, setMaxCards] = useState(10);

  useEffect(() => {
    const calc = () => {
      const desktop = window.innerWidth >= 640;
      setIsDesktop(desktop);
      if (desktop) {
        const padding = 32;
        const available = window.innerWidth - leftOffsetPx - padding;
        setMaxCards(Math.max(1, Math.floor((available + CARD_GAP) / (DESKTOP_CARD_WIDTH + CARD_GAP))));
      } else {
        setMaxCards(10); // horizontal scroll
      }
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [leftOffsetPx]);

  const displaySpots = useMemo(() => spots.slice(0, maxCards), [spots, maxCards]);

  // URL-er som ikke lot seg laste (utløpt CDN-lenke, format nettleseren ikke kan
  // tegne). Nøkles på URL, ikke spot-id, så samme døde områdebilde bare feiler én
  // gang selv når mange plasser deler det.
  const [failedPhotos, setFailedPhotos] = useState<Set<string>>(() => new Set());
  const markPhotoFailed = useCallback((url: string) => {
    setFailedPhotos(prev => (prev.has(url) ? prev : new Set(prev).add(url)));
  }, []);

  // Fetch first UGC image for spots that don't have a photo on the spot document
  const [ugcThumbs, setUgcThumbs] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const spotsNeedingThumb = displaySpots.filter(
      s => !s.ugcThumbnailUrl && !s.photoUrls?.length && !s.auto_image_url
    );
    if (spotsNeedingThumb.length === 0) return;

    (async () => {
      const results: Record<string, string> = {};
      await Promise.all(spotsNeedingThumb.map(async (spot) => {
        try {
          // 1. Try UGC images subcollection first
          const imgQuery = query(
            collection(db, 'cached_spots', spot.id, 'images'),
            orderBy('uploaded_at', 'desc'),
            limit(1)
          );
          const imgSnap = await getDocs(imgQuery);
          if (!imgSnap.empty) {
            const url = imgSnap.docs[0].data().image_url;
            if (url) { results[spot.id] = url; return; }
          }
          // 2. Fallback: check review photos
          const revQuery = query(
            collection(db, 'reviews'),
            where('spotId', '==', spot.id),
            limit(5)
          );
          const revSnap = await getDocs(revQuery);
          for (const d of revSnap.docs) {
            const data = d.data();
            const photo = data.photoUrl || data.photoURL || data.imageUrl || data.imageURL;
            if (photo && typeof photo === 'string' && photo.trim()) {
              results[spot.id] = photo.trim();
              return;
            }
          }
        } catch { /* ignore */ }
      }));
      if (!cancelled && Object.keys(results).length > 0) {
        setUgcThumbs(prev => ({ ...prev, ...results }));
      }
    })();
    return () => { cancelled = true; };
  }, [displaySpots.map(s => s.id).join(',')]);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
    try { localStorage.setItem(PREF_KEYS[detectMiniCardPlatform()], 'false'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (selectedSpotId && selectedCardRef.current && scrollRef.current) {
      selectedCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedSpotId]);

  const stopPropagation = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!visible || (displaySpots.length === 0 && hiddenPremiumCount === 0)) return null;

  const glassStyle = {
    background: 'rgba(20, 24, 24, 0.55)',
    backdropFilter: 'blur(24px) saturate(1.4)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
    border: '1px solid rgba(255,255,255,0.12)',
  };

  return (
    <div
      className="absolute bottom-[72px] sm:bottom-6 left-0 right-0 z-[2100] pointer-events-none px-2 sm:pr-4"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // On desktop, keep clear of the (possibly expanded) sidebar rail so cards never overlap it.
        ...(isDesktop ? { paddingLeft: leftOffsetPx + 16, transition: 'padding-left 200ms ease' } : {}),
      }}
    >
      {/* Dismiss button */}
      <div className="flex justify-start mb-1 pointer-events-auto">
        <button
          onClick={handleDismiss}
          className="flex items-center gap-1 min-h-6 px-2.5 py-1 rounded-full text-3xs font-bold text-white/60 hover:text-white/90 bg-black/30 hover:bg-black/50 backdrop-blur-sm transition-colors"
        >
          <X size={10} />
          <span className="uppercase tracking-wider">{t("auto.map.mapminicards.skjul", "Skjul")}</span>
        </button>
      </div>

      <div
        ref={scrollRef}
        className={isDesktop
          ? 'flex gap-3 pointer-events-auto pb-1'
          : 'flex gap-2 pointer-events-auto pb-1 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide'
        }
        style={!isDesktop ? { WebkitOverflowScrolling: 'touch' } : undefined}
        onTouchStart={stopPropagation}
        onTouchMove={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {displaySpots.map((spot) => {
          const isSelected = spot.id === selectedSpotId;
          const candidate = spot.ugcThumbnailUrl || spot.photoUrls?.[0] || spot.auto_image_url || ugcThumbs[spot.id];
          // Lagrede bilde-URL-er kan være døde — Mapillary-lenkene i auto_image_url er
          // signerte CDN-URL-er som utløper (~12 % svarer 403 i dag), og eldre opplastinger
          // kan være kamera-RAW nettleseren ikke kan tegne. Uten dette ble kortet stående
          // med en tom svart boks i stedet for å falle tilbake til satellittbildet.
          const photo = candidate && !failedPhotos.has(candidate) ? candidate : null;
          const satFallback = !photo ? getSatelliteUrl(spot.location.latitude, spot.location.longitude) : null;
          const pinPos = satFallback ? getSatellitePinPosition(spot.location.latitude, spot.location.longitude) : null;
          const dist = formatDistance(spot, userLocation);

          if (!isDesktop) {
            // Mobile: compact vertical card
            return (
              <div
                key={spot.id}
                ref={isSelected ? selectedCardRef : undefined}
                role="button"
                tabIndex={0}
                aria-label={spot.name || 'Ukjent plass'}
                onClick={() => onSelectSpot(spot.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectSpot(spot.id); } }}
                className={`
                  flex-shrink-0 flex overflow-hidden rounded-xl snap-start
                  transition duration-200 cursor-pointer
                  active:scale-[0.97]
                  ${isSelected
                    ? 'ring-2 ring-emerald-400/70 shadow-[0_4px_20px_rgba(16,185,129,0.25)]'
                    : 'shadow-[0_4px_15px_rgba(0,0,0,0.4)]'
                  }
                `}
                style={{ ...glassStyle, width: '200px', minWidth: '200px' }}
              >
                {/* Photo */}
                <div className="w-16 h-16 shrink-0 bg-black/40 relative overflow-hidden">
                  {photo ? (
                    <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => markPhotoFailed(photo)} />
                  ) : satFallback && pinPos ? (
                    <>
                      <img
                        src={satFallback}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full"
                        width={256}
                        height={256}
                        style={{ objectFit: 'none', objectPosition: `${getSatelliteOffset(pinPos.left, pinPos.top, 64).left}px ${getSatelliteOffset(pinPos.left, pinPos.top, 64).top}px` }}
                      />
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
                        <MapPin size={12} className="text-red-500 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" fill="currentColor" />
                      </div>
                    </>
                  ) : null}
                  {spot.score > 0 && (
                    <div className="absolute top-1 left-1 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded px-1 py-0.5">
                      <Star size={8} className="text-amber-400" fill="currentColor" />
                      <span className="text-4xs font-black text-white">{spot.score.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col justify-between p-2 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {getSpotIcon(spot, 11)}
                    <h3 className="text-sm font-bold text-white truncate text-left leading-tight">
                      {spot.name || 'Ukjent plass'}
                    </h3>
                  </div>

                  <div className="flex items-center justify-between mt-1">
                    {dist && (
                      <span className="text-3xs font-semibold text-[var(--text-muted)]">{dist}</span>
                    )}
                    <button
                      type="button"
                      aria-label={t("auto.map.mapminicards.rute", "Rute")}
                      className="flex items-center gap-0.5 min-h-6 bg-white/15 hover:bg-white/25 rounded-md px-2 py-1 transition-colors ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(spot);
                      }}
                    >
                      <Navigation size={8} className="text-white" />
                      <span className="text-4xs font-bold text-white uppercase tracking-wider">{t("auto.map.mapminicards.rute", "Rute")}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // Desktop: wider horizontal card
          return (
            <div
              key={spot.id}
              ref={isSelected ? selectedCardRef : undefined}
              role="button"
              tabIndex={0}
              aria-label={spot.name || 'Ukjent plass'}
              onClick={() => onSelectSpot(spot.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectSpot(spot.id); } }}
              className={`
                flex-shrink-0 flex overflow-hidden rounded-2xl
                transition duration-200 cursor-pointer
                hover:scale-[1.02] active:scale-[0.98]
                ${isSelected
                  ? 'ring-2 ring-emerald-400/70 shadow-[0_4px_30px_rgba(16,185,129,0.25)]'
                  : 'shadow-[0_4px_20px_rgba(0,0,0,0.4)]'
                }
              `}
              style={{
                ...glassStyle,
                width: `${DESKTOP_CARD_WIDTH}px`,
                minWidth: `${DESKTOP_CARD_WIDTH}px`,
              }}
            >
              {/* Photo / Satellite fallback */}
              <div className="w-24 h-24 shrink-0 bg-black/40 relative overflow-hidden">
                {photo ? (
                  <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => markPhotoFailed(photo)} />
                ) : satFallback && pinPos ? (
                  <>
                    <img
                      src={satFallback}
                      alt=""
                      className="w-full h-full"
                      width={256}
                      height={256}
                      style={{ objectFit: 'none', objectPosition: `${getSatelliteOffset(pinPos.left, pinPos.top, 96).left}px ${getSatelliteOffset(pinPos.left, pinPos.top, 96).top}px` }}
                    />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
                      <MapPin size={16} className="text-red-500 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" fill="currentColor" />
                    </div>
                  </>
                ) : null}
                {!photo && (
                  <div className="absolute bottom-1 left-1 text-[7px] font-bold text-white/50 bg-black/40 rounded px-1">
                    {t("auto.map.mapminicards.satelitt", "Satelitt")}
                  </div>
                )}
                {spot.score > 0 && (
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5">
                    <Star size={10} className="text-amber-400" fill="currentColor" />
                    <span className="text-2xs font-black text-white">{spot.score.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col justify-between p-3 min-w-0">
                <div>
                  <h3 className="text-sm font-bold text-white truncate text-left leading-tight">
                    {spot.name || 'Ukjent plass'}
                  </h3>

                  <div className="flex items-center gap-2 mt-1">
                    {getSpotIcon(spot)}
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: spot.suitabilityColor || '#22c55e' }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-2">
                    {dist && (
                      <span className="text-2xs font-semibold text-[var(--text-muted)]">{dist}</span>
                    )}
                  </div>

                  <button
                    type="button"
                    aria-label={t("auto.map.mapminicards.rute", "Rute")}
                    className="flex items-center gap-1 min-h-6 bg-white/15 hover:bg-white/25 rounded-lg px-2 py-1 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate(spot);
                    }}
                  >
                    <Navigation size={10} className="text-white" />
                    <span className="text-3xs font-bold text-white uppercase tracking-wider">{t("auto.map.mapminicards.rute", "Rute")}</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {hiddenPremiumCount > 0 && (
          <button
            key="__premium_teaser"
            onClick={() => onUpgrade?.()}
            className={`
              flex-shrink-0 flex overflow-hidden rounded-xl sm:rounded-2xl snap-start
              transition duration-200 cursor-pointer
              hover:scale-[1.02] active:scale-[0.97]
              ring-1 ring-emerald-400/40 shadow-[0_4px_20px_rgba(16,185,129,0.25)]
            `}
            style={{
              ...glassStyle,
              background: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(20,24,24,0.65))',
              width: isDesktop ? `${DESKTOP_CARD_WIDTH}px` : '200px',
              minWidth: isDesktop ? `${DESKTOP_CARD_WIDTH}px` : '200px',
            }}
            aria-label={t('auto.map.mapminicards.premium_teaser_aria', 'Vis gratisplasser med premium')}
          >
            {/* Lock-thumbnail */}
            <div className={`${isDesktop ? 'w-24 h-24' : 'w-16 h-16'} shrink-0 bg-emerald-900/30 relative overflow-hidden flex items-center justify-center`}>
              <Lock size={isDesktop ? 28 : 20} className="text-emerald-300" />
              <div className="absolute -top-1 -right-1">
                <Sparkles size={isDesktop ? 14 : 10} className="text-amber-300" />
              </div>
            </div>

            {/* Content */}
            <div className={`flex-1 flex flex-col justify-between ${isDesktop ? 'p-3' : 'p-2'} min-w-0 text-left`}>
              <div>
                <h3 className="text-sm font-bold text-white leading-tight">
                  {t('auto.map.mapminicards.premium_teaser_title', '+{{count}} gratisplasser', { count: hiddenPremiumCount })}
                </h3>
                <p className={`${isDesktop ? 'text-2xs' : 'text-3xs'} text-white/70 mt-0.5 leading-tight`}>
                  {t('auto.map.mapminicards.premium_teaser_sub', 'i nærheten — kun for premium')}
                </p>
              </div>
              <div className={`flex items-center gap-1 bg-brand/30 hover:bg-brand/50 rounded-md px-1.5 py-0.5 self-start mt-1 transition-colors`}>
                <Sparkles size={isDesktop ? 10 : 8} className="text-emerald-200" />
                <span className={`${isDesktop ? 'text-3xs' : 'text-4xs'} font-bold text-emerald-100 uppercase tracking-wider`}>
                  {t('auto.map.mapminicards.premium_teaser_cta', 'Lås opp')}
                </span>
              </div>
            </div>
          </button>
        )}
      </div>

    </div>
  );
};

export default MapMiniCards;

/** Re-enable mini-cards for the current platform */
export const enableMiniCards = () => {
  try { localStorage.setItem(PREF_KEYS[detectMiniCardPlatform()], 'true'); } catch { /* ignore */ }
};

/** Check if mini-cards are enabled for the current platform */
export const isMiniCardsEnabled = (): boolean => getPlatformPref(detectMiniCardPlatform());

/** Get per-platform mini-card prefs */
export const getMiniCardsPlatformPrefs = (): Record<MiniCardPlatform, boolean> => ({
  mobile: getPlatformPref('mobile'),
  tablet: getPlatformPref('tablet'),
  desktop: getPlatformPref('desktop'),
});

/** Set mini-card pref for a specific platform */
export const setMiniCardsPlatformPref = (platform: MiniCardPlatform, enabled: boolean) => {
  try { localStorage.setItem(PREF_KEYS[platform], enabled ? 'true' : 'false'); } catch { /* ignore */ }
};
